import { useCallback, useEffect, useState } from 'react';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { CarouselPaginationPill } from '~/ui-rocco/pages/home-page/components/CarouselPaginationPill';
import { cn } from '@rocco/utils/cn';

interface EditorialCarouselPaginationProps {
    total: number;
    /** Accessible name for the control group. */
    label: string;
    className?: string;
}

/**
 * The dot-and-arrow pill under an editorial carousel.
 *
 * Must render inside `Carousel` — `useCarousel` only resolves there, which is
 * why this is its own component rather than part of the carousels themselves.
 */
export const EditorialCarouselPagination = ({
    total,
    label,
    className,
}: EditorialCarouselPaginationProps) => {
    const { api } = useCarousel();
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (!api) return;
        const sync = () => setActiveIndex(api.selectedScrollSnap());
        sync();
        api.on('select', sync);
        return () => {
            api.off('select', sync);
        };
    }, [api]);

    const onSelect = useCallback(
        (index: number) => api?.scrollTo(index),
        [api],
    );

    return (
        <div
            className={cn(
                'mt-[2.5rem] hidden justify-center md:flex',
                className,
            )}
        >
            <CarouselPaginationPill
                total={total}
                activeIndex={activeIndex}
                onPrev={() => api?.scrollPrev()}
                onNext={() => api?.scrollNext()}
                onSelect={onSelect}
                label={label}
                theme="light"
            />
        </div>
    );
};
